import type { ReactNode } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import ExclamationIconSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/exclamation';
import ExclamationCircleIconSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/exclamation-circle';

import { isDivertedFlight } from '../flightStatusUpdate';
import {
  getStatusBadgeType,
  getStatusLabel,
  shouldUseDivertedStatus,
} from '../utils';

import type { NetSkyscannerArrivaldeparturesvcV1ArrivalDepartureService as ArrivalDepartureTypes } from '@skyscanner-internal/arrival-departure-svc-web-http-client';
import type { FlightStatusDetailsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightStatusDetailsProps';

import STYLES from './FlightStatusBadge.module.scss';

type FlightStatusCode = ArrivalDepartureTypes.FlightStatus;
type FlightStatusUpdate = ArrivalDepartureTypes.FlightStatusUpdate;

type FlightStatusBadgeProps = {
  flight: FlightStatusUpdate | null;
  strings: FlightStatusDetailsProps['strings'];
};

const statusBadgeIconByStatus: Partial<Record<FlightStatusCode, ReactNode>> = {
  FLIGHT_STATUS_CANCELLED: <ExclamationIconSm />,
  FLIGHT_STATUS_DELAYED: <ExclamationIconSm />,
  FLIGHT_STATUS_NO_TAKEOFF_INFO: <ExclamationCircleIconSm />,
};

const FlightStatusBadge = ({ flight, strings }: FlightStatusBadgeProps) => {
  const status = flight?.status;
  const isDiverted = shouldUseDivertedStatus(status, isDivertedFlight(flight));
  const label = status
    ? getStatusLabel(status, strings, isDiverted)
    : strings.FlightTrackerFlight_status_not_available;

  let icon: ReactNode;
  if (isDiverted) {
    icon = <ExclamationIconSm />;
  } else if (status) {
    icon = statusBadgeIconByStatus[status];
  } else {
    icon = null;
  }

  let statusClassNameModifier: string;
  if (isDiverted) {
    statusClassNameModifier = 'diverted';
  } else if (status) {
    statusClassNameModifier = status
      .replace('FLIGHT_STATUS_', '')
      .toLowerCase()
      .replaceAll('_', '-');
  } else {
    statusClassNameModifier = 'not-available';
  }

  return (
    <span className={STYLES.FlightStatusBadge}>
      <BpkBadge
        type={
          status ? getStatusBadgeType(status, isDiverted) : BADGE_TYPES.normal
        }
        className={
          STYLES[`FlightStatusBadge__statusBadge--${statusClassNameModifier}`]
        }
      >
        {icon && <>{icon}&nbsp;</>}
        {label}
      </BpkBadge>
    </span>
  );
};

export default FlightStatusBadge;
